import type { CSSProperties } from 'react';

import { useAgentPromptFeed } from '../ui/match/prompt-feed';

export interface PvpPromptFeedProps {
  mode: 'player' | 'presenter';
  token?: string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

/** The shared player/projector feed. Both paths require their own match-scoped bearer. */
export function PvpPromptFeed({ mode, token = null, baseUrl = '', fetchImpl }: PvpPromptFeedProps) {
  // The poller moved to `ui/match/prompt-feed.ts` (`match-hud` **D-7**) so the Match screen's own
  // prompt cards share it rather than opening a second one with a second cursor. This floating
  // aside is a glance surface, not a history, so it still shows only the last dozen — the trim is
  // display here, where it belongs, rather than a cap on what was fetched.
  const all = useAgentPromptFeed({ mode, token, baseUrl, fetchImpl });
  const prompts = all.slice(-12);

  return (
    <aside data-testid={`pvp-${mode}-prompt-feed`} style={FEED_STYLE} aria-label="Live Match prompt feed">
      <div style={TITLE_STYLE}>{mode === 'presenter' ? 'Both players’ strategies' : 'Live strategies'}</div>
      {mode === 'player' && (
        <a href="/match" style={OVERVIEW_LINK_STYLE}>View Match</a>
      )}
      {prompts.length === 0 ? (
        <div style={EMPTY_STYLE}>Waiting for the first prompt…</div>
      ) : prompts.map((prompt) => (
        <article key={prompt.cursor} style={ENTRY_STYLE}>
          <div style={META_STYLE}>
            <span style={{ color: `var(--colony-${prompt.colony === 0 ? 0 : 1})` }}>{prompt.name}</span>
            <span>{prompt.colony === 0 ? 'Red' : 'Blue'} · tick {prompt.tick}</span>
          </div>
          <div>{prompt.text}</div>
        </article>
      ))}
    </aside>
  );
}

const FEED_STYLE: CSSProperties = {
  position: 'fixed', top: 16, right: 16, zIndex: 80, width: 'min(360px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 32px)', overflowY: 'auto', padding: 14, borderRadius: 'var(--r-lg)',
  color: 'var(--surface-card)', background: 'var(--ink-1)', border: '1px solid var(--line-strong)',
  boxShadow: 'var(--shadow-card)', fontSize: 13,
};
const TITLE_STYLE: CSSProperties = { fontWeight: 700, fontSize: 15, marginBottom: 10 };
const OVERVIEW_LINK_STYLE: CSSProperties = { display: 'inline-flex', alignItems: 'center', minHeight: 'var(--hit-target)', color: 'var(--accent)', fontSize: 11, margin: '-5px 0 10px' };
const EMPTY_STYLE: CSSProperties = { opacity: 0.7 };
const ENTRY_STYLE: CSSProperties = { padding: '9px 0', borderTop: '1px solid var(--line-strong)' };
const META_STYLE: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 8, opacity: 0.78, fontSize: 11, marginBottom: 4 };
